import ReactDOM from 'react-dom';
import { PropsWithChildren, FC, useEffect, useRef } from 'react';
import { SerializedStyles } from '@emotion/react';
import { ErrorBoundary } from '@/library/components/general/ErrorBoundary';

import usePortal from '@/library/utils/hooks/usePortal.ts';
import SvgRender from '@/library/components/general/SvgRender';
import closeIcon from '@/library/assets/svg/actions/close.svg';

export type DrawerProps = PropsWithChildren<{
  isOpen: boolean;
  close?: () => void;
  className?: string;
  style?: React.CSSProperties;
  size?: 'sm' | 'sm-md' | 'md' | 'md-lg' | 'lg';
  css?: SerializedStyles;
}>;

const Drawer: FC<DrawerProps> = ({
  isOpen,
  close,
  children,
  className = '',
  style,
  size = 'sm',
  css
}) => {
  const target = usePortal('drawer-container');
  const ref = useRef(null);

  useEffect(() => {
    const openedDrawers = document.querySelectorAll('.drawer.open');

    if (!isOpen && !openedDrawers.length) {
      document.body.classList.remove('drawer-open');
    } else if (isOpen) {
      document.body.classList.add('drawer-open');
    }

    // Cleanup: ensure body class is removed if component unmounts while open
    return () => {
      if (isOpen) {
        const remainingDrawers = document.querySelectorAll('.drawer.open');
        if (remainingDrawers.length === 0) {
          document.body.classList.remove('drawer-open');
        }
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const tooltips = document.querySelectorAll('.tooltip');
      tooltips.forEach(tooltip => tooltip.remove());
    }
  }, [isOpen]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      id="drawer"
      className={`drawer drawer-size--${size} ${isOpen ? 'open' : ''} ${className}`}
      style={style}
      css={css}
    >
      <div id="drawer__close" className="drawer__close cursor-pointer me-4" onClick={close}>
        <SvgRender src={closeIcon} style={{ width: 14, height: 14 }} className="pe-none" />
      </div>
      {isOpen ? (
        <ErrorBoundary
          fallback={<div className="fs-12 text-primary pt-6 ps-3">Something went wrong.</div>}
        >
          {children}
        </ErrorBoundary>
      ) : null}
    </div>,
    target
  );
};
export default Drawer;

type DrawerComponentProps = PropsWithChildren<{
  className?: string;
}>;

export const DrawerHeader = ({ children, ...rest }: DrawerComponentProps) => (
  <div id="drawer__header" {...rest}>
    {children}
  </div>
);

type DrawerBodyComponentProps = DrawerComponentProps & {
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
};

export const DrawerBody = ({ children, ...rest }: DrawerBodyComponentProps) => (
  <div id="drawer__body" className="gray-scrollbar  overflow-x-hidden" {...rest}>
    {children}
  </div>
);
export const DrawerFooter = ({ children, ...rest }: DrawerComponentProps) => (
  <div id="drawer__footer" {...rest}>
    {children}
  </div>
);
